import React, { useEffect, useState } from 'react';
import { safeApiCall } from '../../../utils/apiHelpers';
import { formatLongDate, formatLongDateTime } from '../../../utils/dateFormat';
import { fetchTeamLeaveRequests, approveOrRejectLeaveRequest, postLeaveRequest } from './api';
import { fetchCollection } from '../../../api/common';
import ApplyLeaveModal from '../MyData/ApplyLeaveModal';

const statusOptions = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

const TeamLeaveRequests = () => {
  const [datesModalOpen, setDatesModalOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDatesTitle, setSelectedDatesTitle] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState(['Pending']);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [collection, setCollection] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlapList, setOverlapList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  useEffect(() => {
    (async () => {
      setCollectionLoading(true);
      const data = await safeApiCall(fetchCollection, ['leave_types', 'leave_day_type', 'holidays']);
      if (data) setCollection(data);
      setCollectionLoading(false);
    })();
  }, []);

  const fetchRequests = async (page = pagination.page, limit = pagination.limit) => {
    setLoading(true);
    try {
      const searchTerms = searchTerm.split(',').map(term => term.trim()).filter(term => term);
      const res = await safeApiCall(fetchTeamLeaveRequests, statuses, page, limit, searchTerms);
      if (res && res.data) {
        // Handle both the new pagination format and fallback to old format if needed
        if (res.data.results !== undefined) {
          // New pagination format
          setRequests(res.data.results || []);
          const total = res.data.count || 0;
          const totalPages = Math.ceil(total / limit) || 1;
          
          setPagination(prev => ({
            ...prev,
            page: page,
            total: total,
            totalPages: totalPages
          }));
        } else {
          // Fallback to old format if needed
          setRequests(Array.isArray(res.data) ? res.data : []);
          setPagination(prev => ({
            ...prev,
            page: 1,
            total: res.data.length || 0,
            totalPages: 1
          }));
        }
      } else {
        setRequests([]);
        setPagination(prev => ({
          ...prev,
          page: 1,
          total: 0,
          totalPages: 1
        }));
      }
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      setRequests([]);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(1);
  }, [statuses]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchRequests(newPage);
    }
  };

  const handleStatusChange = (status) => {
    setStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const reportingManagerId = parseInt(localStorage.getItem('reporting_manager'));

  const handleApplyLeave = async (payload) => {
    setLoading(true);
    const postRes = await safeApiCall(postLeaveRequest, payload);
    if (postRes !== null) {
      setShowApplyModal(false);
      await fetchRequests(pagination.page);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Team Leave Tracker</h2>
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-4 items-center">
            {statusOptions.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={statuses.includes(opt.value)}
                  onChange={() => handleStatusChange(opt.value)}
                  className="accent-blue-600 w-4 h-4 rounded"
                />
                {opt.label}
              </label>
            ))}
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchRequests()}
              placeholder="Search by name (comma separated)"
              className="pl-4 pr-10 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => fetchRequests()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              title="Search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <hr className="mb-4" />
      {showApplyModal && collection && (
        <ApplyLeaveModal
          open={showApplyModal}
          onClose={() => setShowApplyModal(false)}
          onSubmit={handleApplyLeave}
          collection={collection}
          reportingManagerId={reportingManagerId}
        />
      )}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            Showing {requests.length} of {pagination.total} requests
          </span>
          <select
            value={pagination.limit}
            onChange={(e) => {
              setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }));
              fetchRequests(1, Number(e.target.value));
            }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
            className={`px-3 py-1 rounded ${
              pagination.page === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className={`px-3 py-1 rounded ${
              pagination.page <= 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            Previous
          </button>
          <span className="px-3 py-1 bg-gray-100 rounded">
            {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className={`px-3 py-1 rounded ${
              pagination.page >= pagination.totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
            className={`px-3 py-1 rounded ${
              pagination.page === pagination.totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            Last
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full text-sm bg-white border-separate border-spacing-y-1">
          <thead>
            <tr className="bg-slate-100 text-gray-700">
              <th className="px-4 py-3 text-left rounded-tl-lg">Status</th>
              <th className="px-4 py-3 text-left">Employee</th>
              <th className="px-4 py-3 text-left">Leave Type</th>
              <th className="px-4 py-3 text-left">Reason</th>
              <th className="px-4 py-3 text-left">Requested On</th>
              <th className="px-4 py-3 text-left">Leave Dates</th>
              <th className="px-4 py-3 text-center">Overlaps</th>
              <th className="px-4 py-3 text-left rounded-tr-lg">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center p-6 text-gray-500">Loading...</td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-6 text-gray-400">No leave requests found.</td>
              </tr>
            ) : (
              requests.map(req => (
                <tr key={req.id} className="bg-gray-50 hover:bg-blue-50 transition">
                  <td>
                    <span className={
                      `inline-block px-3 py-1 rounded-full text-xs font-bold
                      ${req.status_of_leave === 'Approved' ? 'bg-green-100 text-green-700' :
                        req.status_of_leave === 'Rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'}`
                    }>
                      {req.status_of_leave}
                    </span>
                  </td>
                  <td className="px-4 py-2">{req.employee_full_name}</td>
                  <td className="px-4 py-2">{req.leave_type_name}</td>
                  <td className="px-4 py-2">{req.reason_for_leave}</td>
                  <td className="px-4 py-2">{formatLongDateTime(req.requested_at)}</td>
                  <td className="px-4 py-2">
                    {req.dates.length > 1 ? (
                      <button
                        className="text-blue-600 underline font-medium cursor-pointer"
                        type="button"
                        onClick={() => {
                          setSelectedDates(req.dates);
                          setSelectedDatesTitle(`${req.dates.length} Dates`);
                          setDatesModalOpen(true);
                        }}
                      >
                        {req.dates.length} Dates
                      </button>
                    ) : req.dates.length === 1 ? (
                      <div>{formatLongDate(req.dates[0].date)} ({req.dates[0].leave_day_type})</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {req.overlapping_leaves?.length > 0 ? (
                      <button onClick={() => { setOverlapList(req.overlapping_leaves); setOverlapModalOpen(true); }} className="text-blue-600 underline">
                        {req.overlapping_leaves.length}
                      </button>
                    ) : '0'}
                  </td>
                  <td className="px-4 py-2">
                    {req.status_of_leave === 'Pending' ? (
                      <>
                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-full mr-2 transition"
                          onClick={async () => {
                            const res = await safeApiCall(approveOrRejectLeaveRequest, req.id, 'Approved');
                            if (res !== null) {
                              setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status_of_leave: 'Approved' } : r));
                            }
                          }}
                        >Approve</button>
                        <button
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-full transition"
                          onClick={async () => {
                            const res = await safeApiCall(approveOrRejectLeaveRequest, req.id, 'Rejected');
                            if (res !== null) {
                              setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status_of_leave: 'Rejected' } : r));
                            }
                          }}
                        >Reject</button>
                      </>
                    ) : (
                      <span className="font-semibold">{req.status_of_leave}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {overlapModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Overlapping Leaves</h3>
            <ul className="space-y-2 mb-4">
              {overlapList.map(item => (
                <li key={item.id} className="border-b pb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{item.employee_full_name}</span>
                    <span className="text-sm font-medium">{item.leave_type_name}</span>
                  </div>
                  <div className="text-sm text-gray-600">{item.dates.map(d => formatLongDate(d.date)).join(', ')}</div>
                </li>
              ))}
            </ul>
            <button onClick={() => setOverlapModalOpen(false)} className="mt-2 bg-blue-600 text-white px-4 py-2 rounded">Close</button>
          </div>
        </div>
      )}
      {datesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">{selectedDatesTitle}</h3>
            <ul className="space-y-2 mb-4">
              {selectedDates.map(d => (
                <li key={d.id} className="border-b pb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{formatLongDate(d.date)}</span>
                    <span className="text-sm font-medium">{d.leave_day_type}</span>
                  </div>
                </li>
              ))}
            </ul>
            <button onClick={() => setDatesModalOpen(false)} className="mt-2 bg-blue-600 text-white px-4 py-2 rounded">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamLeaveRequests;
